import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, ListSubheader, CircularProgress, Autocomplete } from "@mui/material";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toBrasiliaWallClock, instantToBrasiliaInputValue } from "../../helpers/brasiliaWallClock";

/**
 * Campos de "novo chamado GLPI" (cliente/entidade, filial, categoria, tipo,
 * classificação, requerente do grupo, tempo de trabalho) compartilhados entre
 * GlpiCloseDialog e GlpiReturnDialog. O componente é autocontido — mantém seu
 * próprio estado e reporta os valores atuais e a validade via `onChange`.
 */
const GlpiEntityFields = ({ open, ticket, onChange, showCategoryFields = true, taskEntries = [] }) => {
	const [glpiCategoryId, setGlpiCategoryId] = useState("");
	const [glpiWorkHours, setGlpiWorkHours] = useState("");
	const [glpiWorkMinutes, setGlpiWorkMinutes] = useState("");
	const [glpiType, setGlpiType] = useState("");
	const [categories, setCategories] = useState([]);
	const [entities, setEntities] = useState([]);
	const [glpiEntityId, setGlpiEntityId] = useState(null);
	const [createEntityOpen, setCreateEntityOpen] = useState(false);
	const [newEntityName, setNewEntityName] = useState("");
	const [newEntityCnpj, setNewEntityCnpj] = useState("");
	const [loadingCreateEntity, setLoadingCreateEntity] = useState(false);
	const [subEntities, setSubEntities] = useState([]);
	const [glpiSubEntityId, setGlpiSubEntityId] = useState(null);
	const [loadingSubEntities, setLoadingSubEntities] = useState(false);
	const [createSubEntityOpen, setCreateSubEntityOpen] = useState(false);
	const [newSubEntityName, setNewSubEntityName] = useState("");
	const [newSubEntityCnpj, setNewSubEntityCnpj] = useState("");
	const [loadingCreateSubEntity, setLoadingCreateSubEntity] = useState(false);
	const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState("");
	const [newCategoryParentId, setNewCategoryParentId] = useState("");
	const [loadingCreateCategory, setLoadingCreateCategory] = useState(false);
	const [glpiRequesterId, setGlpiRequesterId] = useState(null);
	const [glpiRequesterOptions, setGlpiRequesterOptions] = useState([]);
	const [loadingRequesters, setLoadingRequesters] = useState(false);
	const [glpiServiceDate, setGlpiServiceDate] = useState("");
	const [hasAcceptedAt, setHasAcceptedAt] = useState(false);
	const [glpiMinTime, setGlpiMinTime] = useState(0);
	const [glpiClassificationId, setGlpiClassificationId] = useState("");
	const [glpiName, setGlpiName] = useState("");
	const [classifications, setClassifications] = useState([]);
	const [createClassificationOpen, setCreateClassificationOpen] = useState(false);
	const [newClassificationName, setNewClassificationName] = useState("");
	const [loadingCreateClassification, setLoadingCreateClassification] = useState(false);

	// Lido dentro de callbacks assíncronos (applyAcceptedAt) para saber, no
	// momento em que a resposta chega, se já existem atividades registradas —
	// evita que o tempo decorrido desde o aceite sobrescreva a soma das
	// atividades quando a resposta do "aceite" chega depois da das atividades.
	const taskEntriesRef = useRef(taskEntries);
	taskEntriesRef.current = taskEntries;

	useEffect(() => {
		if (!open || !ticket) return;

		setGlpiCategoryId("");
		setCategories([]);
		setGlpiWorkHours("");
		setGlpiWorkMinutes("");
		setGlpiType("");
		setGlpiEntityId(null);
		setGlpiSubEntityId(null);
		setSubEntities([]);
		setGlpiRequesterId(null);
		setGlpiRequesterOptions([]);
		setGlpiServiceDate("");
		setGlpiClassificationId("");
		setGlpiName("");
		setHasAcceptedAt(false);

		const applyAcceptedAt = (acceptedAt, minTime) => {
			if (!acceptedAt) return;
			setHasAcceptedAt(true);
			// Atividades registradas têm prioridade — não sobrescreve o tempo já
			// calculado a partir delas caso essa resposta assíncrona chegue depois.
			if (taskEntriesRef.current.length > 0) return;
			const diffMs = Date.now() - new Date(acceptedAt).getTime();
			const rawMins = Math.floor(diffMs / 60000);
			const effectiveMins = minTime > 0 ? Math.max(rawMins, minTime) : rawMins;
			setGlpiWorkHours(String(Math.floor(effectiveMins / 60)));
			setGlpiWorkMinutes(String(effectiveMins % 60));
			// Sugere o início do atendimento como data/hora de início — evita que o
			// atendente deixe o campo em branco e o navegador caia em now(), o que
			// fazia atendimentos dentro do expediente serem registrados como fora
			// da cobertura de contrato quando o fechamento acontecia depois.
			setGlpiServiceDate(instantToBrasiliaInputValue(acceptedAt));
		};

		api.get("/integrations").then(({ data }) => {
			const get = key => {
				const row = data.find(i => i.key === key);
				return row ? parseInt(row.value) || 0 : 0;
			};
			const minTime = get("glpiMinTime");
			setGlpiMinTime(minTime);
			api.get(`/tickets/${ticket.id}`).then(({ data: t }) => {
				applyAcceptedAt(t.acceptedAt, minTime);
			}).catch(() => {
				applyAcceptedAt(ticket.acceptedAt, minTime);
			});
		}).catch(() => {
			api.get(`/tickets/${ticket.id}`).then(({ data: t }) => {
				applyAcceptedAt(t.acceptedAt, 0);
			}).catch(() => {
				applyAcceptedAt(ticket.acceptedAt, 0);
			});
		});

		api.get("/glpi/classifications").then(({ data }) => {
			setClassifications(Array.isArray(data) ? data : []);
		}).catch(() => setClassifications([]));

		api.get("/glpi/entities/root").then(({ data }) => {
			setEntities(Array.isArray(data) ? data : []);
		}).catch(() => setEntities([]));

		const contactEntityId = ticket.contact?.glpiEntityId;
		if (contactEntityId) {
			api.get(`/glpi/entities/${contactEntityId}`).then(async ({ data: entityData }) => {
				let resolvedEntityId = contactEntityId;
				if (entityData.entities_id === 0) {
					setGlpiEntityId(contactEntityId);
				} else {
					setGlpiEntityId(entityData.entities_id);
					setGlpiSubEntityId(contactEntityId);
					resolvedEntityId = contactEntityId;
					try {
						const { data: children } = await api.get(`/glpi/entities/${entityData.entities_id}/children`);
						setSubEntities(Array.isArray(children) ? children : []);
					} catch {}
				}
				if (showCategoryFields) {
					try {
						const { data: cats } = await api.get(`/glpi/categories?entityId=${resolvedEntityId}&isIncident=1`);
						setCategories(Array.isArray(cats) ? cats : []);
					} catch { setCategories([]); }
				}
			}).catch(() => setGlpiEntityId(contactEntityId));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, ticket]);

	const finalEntityId = glpiSubEntityId || glpiEntityId;
	const hasTaskEntries = taskEntries.length > 0;

	// Quando há tarefas registradas durante o atendimento (Registrar Tarefa /
	// Registrar Período de Tarefa), o tempo de trabalho e a data de início
	// passam a ser só um resumo somado das entries — o envio real ao GLPI usa
	// cada entry individualmente (begin/end reais), não esse agregado.
	useEffect(() => {
		if (!hasTaskEntries) return;
		const totalSeconds = taskEntries.reduce((acc, e) => acc + (e.durationSeconds || 0), 0);
		const totalMins = Math.floor(totalSeconds / 60);
		setGlpiWorkHours(String(Math.floor(totalMins / 60)));
		setGlpiWorkMinutes(String(totalMins % 60));
		const earliest = taskEntries.reduce(
			(min, e) => (!min || new Date(e.startAt) < new Date(min.startAt) ? e : min),
			null
		);
		if (earliest) setGlpiServiceDate(instantToBrasiliaInputValue(earliest.startAt));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskEntries]);

	useEffect(() => {
		if (!onChange) return;
		const h = parseInt(glpiWorkHours) || 0;
		const m = parseInt(glpiWorkMinutes) || 0;
		const actiontimeSecs = (h * 60 + m) * 60 || undefined;
		const isValid = Boolean(
			glpiServiceDate &&
			finalEntityId &&
			(!showCategoryFields || (glpiType && glpiCategoryId)) &&
			(!ticket?.isGroup || glpiRequesterId)
		);
		onChange({
			glpiEntityId: finalEntityId ? Number(finalEntityId) : null,
			glpiCategoryId: glpiCategoryId ? Number(glpiCategoryId) : null,
			glpiType: glpiType ? Number(glpiType) : null,
			glpiClassificationId: glpiClassificationId ? Number(glpiClassificationId) : null,
			glpiRequesterId: glpiRequesterId ?? null,
			glpiActiontime: actiontimeSecs,
			glpiServiceDate: toBrasiliaWallClock(glpiServiceDate),
			glpiName: glpiName.trim() || null,
			isValid
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [finalEntityId, glpiCategoryId, glpiType, glpiClassificationId, glpiRequesterId, glpiWorkHours, glpiWorkMinutes, glpiServiceDate, glpiName]);

	const handleServiceDateChange = (value) => {
		setGlpiServiceDate(value);
		if (!value) return;
		const startMs = new Date(value).getTime();
		if (Number.isNaN(startMs)) return;
		const diffMs = Date.now() - startMs;
		const rawMins = Math.max(0, Math.floor(diffMs / 60000));
		const effectiveMins = glpiMinTime > 0 ? Math.max(rawMins, glpiMinTime) : rawMins;
		setGlpiWorkHours(String(Math.floor(effectiveMins / 60)));
		setGlpiWorkMinutes(String(effectiveMins % 60));
	};

	const searchRequesterContacts = async (val) => {
		if (!val || val.length < 2) return;
		setLoadingRequesters(true);
		try {
			const { data } = await api.get(`/contacts?searchParam=${encodeURIComponent(val)}&pageNumber=1`);
			setGlpiRequesterOptions(data.contacts || []);
		} catch {} finally { setLoadingRequesters(false); }
	};

	const handleEntityChange = async (entityId) => {
		setGlpiEntityId(entityId);
		setGlpiSubEntityId(null);
		setSubEntities([]);
		setCategories([]);
		setGlpiCategoryId("");
		if (!entityId) return;

		setLoadingSubEntities(true);
		try {
			const requests = [
				api.get(`/glpi/entities/${entityId}/children`),
				...(showCategoryFields ? [api.get(`/glpi/categories?entityId=${entityId}&isIncident=1`)] : [])
			];
			const [childrenRes, catsRes] = await Promise.allSettled(requests);
			if (childrenRes.status === "fulfilled") {
				setSubEntities(Array.isArray(childrenRes.value.data) ? childrenRes.value.data : []);
			}
			if (catsRes && catsRes.status === "fulfilled") {
				setCategories(Array.isArray(catsRes.value.data) ? catsRes.value.data : []);
			}
		} catch {
			setSubEntities([]);
		} finally {
			setLoadingSubEntities(false);
		}
	};

	const handleCreateEntity = async () => {
		if (!newEntityName.trim()) return;
		setLoadingCreateEntity(true);
		try {
			const { data } = await api.post("/glpi/entities", {
				name: newEntityName.trim(),
				cnpj: newEntityCnpj.trim() || undefined
			});
			const newEntity = { id: data.id, name: newEntityName.trim(), completename: newEntityName.trim() };
			setEntities(prev => [...prev, newEntity]);
			await handleEntityChange(data.id);
			setCreateEntityOpen(false);
			setNewEntityName("");
			setNewEntityCnpj("");
			toast.success("Cliente criado no GLPI.");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCreateEntity(false);
		}
	};

	const handleCreateSubEntity = async () => {
		if (!newSubEntityName.trim() || !glpiEntityId) return;
		setLoadingCreateSubEntity(true);
		try {
			const { data } = await api.post(`/glpi/entities/${glpiEntityId}/children`, {
				name: newSubEntityName.trim(),
				cnpj: newSubEntityCnpj.trim() || undefined
			});
			const newSub = { id: data.id, name: newSubEntityName.trim(), completename: newSubEntityName.trim() };
			setSubEntities(prev => [...prev, newSub]);
			setGlpiSubEntityId(data.id);
			setCreateSubEntityOpen(false);
			setNewSubEntityName("");
			setNewSubEntityCnpj("");
			toast.success("Filial criada no GLPI.");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCreateSubEntity(false);
		}
	};

	const handleCreateCategory = async () => {
		if (!newCategoryName.trim()) return;
		setLoadingCreateCategory(true);
		try {
			const { data } = await api.post("/glpi/categories", {
				name: newCategoryName.trim(),
				entityId: glpiEntityId || 0,
				parentId: newCategoryParentId || undefined
			});
			const newCat = {
				id: data.id,
				name: newCategoryName.trim(),
				completename: newCategoryName.trim(),
				entities_id: glpiEntityId || 0
			};
			setCategories(prev => [...prev, newCat]);
			setGlpiCategoryId(data.id);
			setCreateCategoryOpen(false);
			setNewCategoryName("");
			setNewCategoryParentId("");
			toast.success("Categoria criada no GLPI.");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCreateCategory(false);
		}
	};

	const handleCreateClassification = async () => {
		if (!newClassificationName.trim()) return;
		setLoadingCreateClassification(true);
		try {
			const { data } = await api.post("/glpi/classifications", { name: newClassificationName.trim() });
			const newClass = { id: data.id, name: newClassificationName.trim() };
			setClassifications(prev => [...prev, newClass]);
			setGlpiClassificationId(data.id);
			setCreateClassificationOpen(false);
			setNewClassificationName("");
			toast.success("Classificação criada no GLPI.");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCreateClassification(false);
		}
	};

	const rootNames = new Set(
		categories.flatMap(c => {
			const parts = c.completename.split(" > ");
			return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join(" > "));
		})
	);
	const leafCategories = categories.filter(c => !rootNames.has(c.completename));
	const groupedCategories = leafCategories.reduce((acc, c) => {
		const parts = c.completename.split(" > ");
		const parent = parts.length > 1 ? parts.slice(0, -1).join(" > ") : "";
		if (!acc[parent]) acc[parent] = [];
		acc[parent].push(c);
		return acc;
	}, {});

	if (!ticket) return null;

	return (
		<>
			{ticket.isGroup && (
				<Autocomplete
					size="small"
					options={glpiRequesterOptions}
					loading={loadingRequesters}
					getOptionLabel={(o) => `${o.name}${o.number ? ` (${o.number})` : ""}`}
					isOptionEqualToValue={(o, v) => o.id === v.id}
					filterOptions={(x) => x}
					onInputChange={(_, val, reason) => { if (reason === "input") searchRequesterContacts(val); }}
					onChange={(_, v) => setGlpiRequesterId(v?.id ?? null)}
					renderInput={(params) => (
						<TextField {...params} label="Requerente *" variant="outlined" size="small"
							helperText="Digite nome ou telefone do contato que será o requerente no GLPI"
							error={!glpiRequesterId}
							InputProps={{ ...params.InputProps, endAdornment: (<>{loadingRequesters ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
						/>
					)}
					noOptionsText={loadingRequesters ? "Buscando..." : "Digite para buscar contatos"}
				/>
			)}
			<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
				<Autocomplete
					sx={{ flex: 1 }}
					size="small"
					options={entities}
					getOptionLabel={opt => opt.completename || opt.name || ""}
					value={entities.find(e => e.id === glpiEntityId) || null}
					onChange={(_, val) => handleEntityChange(val ? val.id : null)}
					renderInput={params => (
						<TextField {...params}
							label="Cliente *"
							variant="outlined"
							placeholder="Selecione ou busque..."
							error={!glpiEntityId}
						/>
					)}
					noOptionsText="Nenhum cliente encontrado"
					isOptionEqualToValue={(opt, val) => opt.id === val.id}
				/>
				<Tooltip title="Criar novo cliente no GLPI" arrow>
					<IconButton size="small" onClick={() => setCreateEntityOpen(true)} color="primary">
						<AddIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			{glpiEntityId && (
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					<Autocomplete
						sx={{ flex: 1 }}
						size="small"
						options={subEntities}
						loading={loadingSubEntities}
						getOptionLabel={opt => opt.name || ""}
						value={subEntities.find(e => e.id === glpiSubEntityId) || null}
						onChange={async (_, val) => {
							const subId = val ? val.id : null;
							setGlpiSubEntityId(subId);
							setGlpiCategoryId("");
							const targetId = subId || glpiEntityId;
							if (targetId && showCategoryFields) {
								try {
									const { data: cats } = await api.get(`/glpi/categories?entityId=${targetId}&isIncident=1`);
									setCategories(Array.isArray(cats) ? cats : []);
								} catch { setCategories([]); }
							}
						}}
						renderInput={params => (
							<TextField {...params} label="Filial" variant="outlined" placeholder={loadingSubEntities ? "Carregando..." : subEntities.length === 0 ? "Sem filiais cadastradas" : "Selecione a filial..."} />
						)}
						noOptionsText="Sem filiais cadastradas"
						isOptionEqualToValue={(opt, val) => opt.id === val.id}
					/>
					<Tooltip title="Criar nova filial no GLPI" arrow>
						<IconButton size="small" onClick={() => setCreateSubEntityOpen(true)} color="primary">
							<AddIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			)}
			<TextField
				type="datetime-local"
				fullWidth
				size="small"
				variant="outlined"
				label="Data/hora de Inicio do atendimento"
				value={glpiServiceDate}
				onChange={e => handleServiceDateChange(e.target.value)}
				InputLabelProps={{ shrink: true }}
				disabled={hasTaskEntries}
			/>
			<Box>
				<Box sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 0.5 }}>
					Tempo de Trabalho
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					<TextField
						type="number"
						size="small"
						variant="outlined"
						label="Horas"
						value={glpiWorkHours}
						onChange={e => setGlpiWorkHours(e.target.value.replace(/\D/g, ""))}
						inputProps={{ min: 0, max: 99 }}
						sx={{ width: 130 }}
						disabled={hasTaskEntries}
					/>
					<TextField
						type="number"
						size="small"
						variant="outlined"
						label="Minutos"
						value={glpiWorkMinutes}
						onChange={e => {
							const v = parseInt(e.target.value) || 0;
							setGlpiWorkMinutes(String(Math.min(59, Math.max(0, v))));
						}}
						inputProps={{ min: 0, max: 59 }}
						sx={{ width: 130 }}
						disabled={hasTaskEntries}
					/>
				</Box>
				{hasTaskEntries ? (
					<Box sx={{ mt: 0.5, fontSize: "0.75rem", color: "text.secondary" }}>
						Calculado a partir das tarefas registradas neste atendimento
					</Box>
				) : (hasAcceptedAt || glpiServiceDate) && (
					<Box sx={{ mt: 0.5, fontSize: "0.75rem", color: "text.secondary" }}>
						Calculado a partir do início do atendimento — edite para sobrescrever
					</Box>
				)}
			</Box>
			{showCategoryFields && (<>
			<TextField
				fullWidth
				variant="outlined"
				size="small"
				label="Nome do chamado (opcional)"
				placeholder={`Será usado como [AD Ticket #${ticket?.id ?? "N"}] <nome>`}
				value={glpiName}
				onChange={e => setGlpiName(e.target.value)}
			/>
			<TextField
				select
				fullWidth
				variant="outlined"
				size="small"
				label="Tipo"
				value={glpiType}
				onChange={e => setGlpiType(e.target.value)}
				required
			>
				<MenuItem value="">— Selecione o tipo —</MenuItem>
				<MenuItem value="1">Incidente</MenuItem>
				<MenuItem value="2">Requisição</MenuItem>
			</TextField>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<TextField
					select
					fullWidth
					variant="outlined"
					size="small"
					label="Categoria"
					value={glpiCategoryId}
					onChange={e => setGlpiCategoryId(e.target.value)}
					required
				>
					<MenuItem value="">— Selecione a categoria —</MenuItem>
					{Object.entries(groupedCategories).map(([parent, children]) => [
						parent ? (
							<ListSubheader key={`hdr-${parent}`} disableSticky>{parent}</ListSubheader>
						) : null,
						children.map(c => (
							<MenuItem key={c.id} value={c.id} sx={{ pl: parent ? 3 : 2 }}>{c.name}</MenuItem>
						))
					])}
				</TextField>
				<Tooltip title="Criar nova categoria no GLPI" arrow>
					<IconButton size="small" onClick={() => setCreateCategoryOpen(true)} color="primary">
						<AddIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
				<TextField
					select
					fullWidth
					variant="outlined"
					size="small"
					label="Classificação"
					value={glpiClassificationId}
					onChange={e => setGlpiClassificationId(e.target.value)}
				>
					<MenuItem value="">— Selecione a classificação —</MenuItem>
					{classifications.map(c => (
						<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
					))}
				</TextField>
				<Tooltip title="Criar nova classificação no GLPI" arrow>
					<IconButton size="small" onClick={() => setCreateClassificationOpen(true)} color="primary">
						<AddIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			</>)}

			<Dialog open={createEntityOpen} onClose={() => setCreateEntityOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Novo cliente no GLPI</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						autoFocus
						fullWidth
						size="small"
						variant="outlined"
						label="Nome / Razão Social *"
						value={newEntityName}
						onChange={e => setNewEntityName(e.target.value)}
						sx={{ mt: 1 }}
					/>
					<TextField
						fullWidth
						size="small"
						variant="outlined"
						label="CNPJ (opcional)"
						value={newEntityCnpj}
						onChange={e => setNewEntityCnpj(e.target.value)}
						placeholder="00.000.000/0001-00"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateEntityOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleCreateEntity}
						disabled={!newEntityName.trim() || loadingCreateEntity}
					>
						{loadingCreateEntity ? "Criando..." : "Criar cliente"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={createSubEntityOpen} onClose={() => setCreateSubEntityOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Nova filial no GLPI</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						autoFocus
						fullWidth
						size="small"
						variant="outlined"
						label="Nome da filial *"
						value={newSubEntityName}
						onChange={e => setNewSubEntityName(e.target.value)}
						sx={{ mt: 1 }}
					/>
					<TextField
						fullWidth
						size="small"
						variant="outlined"
						label="CNPJ (opcional)"
						value={newSubEntityCnpj}
						onChange={e => setNewSubEntityCnpj(e.target.value)}
						placeholder="00.000.000/0001-00"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateSubEntityOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleCreateSubEntity}
						disabled={!newSubEntityName.trim() || loadingCreateSubEntity}
					>
						{loadingCreateSubEntity ? "Criando..." : "Criar filial"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={createCategoryOpen} onClose={() => setCreateCategoryOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Nova categoria no GLPI</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						autoFocus
						fullWidth
						size="small"
						variant="outlined"
						label="Nome da categoria *"
						value={newCategoryName}
						onChange={e => setNewCategoryName(e.target.value)}
					/>
					<TextField
						select
						fullWidth
						size="small"
						variant="outlined"
						label="Categoria pai (opcional)"
						value={newCategoryParentId}
						onChange={e => setNewCategoryParentId(e.target.value)}
					>
						<MenuItem value="">Nenhuma (categoria raiz)</MenuItem>
						{categories.filter(c => rootNames.has(c.completename)).map(cat => (
							<MenuItem key={cat.id} value={cat.id}>{cat.completename || cat.name}</MenuItem>
						))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateCategoryOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleCreateCategory}
						disabled={!newCategoryName.trim() || loadingCreateCategory}
					>
						{loadingCreateCategory ? "Criando..." : "Criar categoria"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={createClassificationOpen} onClose={() => setCreateClassificationOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Nova classificação no GLPI</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
					<TextField
						autoFocus
						fullWidth
						size="small"
						variant="outlined"
						label="Nome da classificação *"
						value={newClassificationName}
						onChange={e => setNewClassificationName(e.target.value)}
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateClassificationOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleCreateClassification}
						disabled={!newClassificationName.trim() || loadingCreateClassification}
					>
						{loadingCreateClassification ? "Criando..." : "Criar classificação"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default GlpiEntityFields;
